import { Flex } from "@chakra-ui/react";
import React, { useEffect, useRef } from 'react';
import { ProximityChatMessage } from "../../../../../services/townService/src/CoveyTypes";
import { ServerPlayer, UserLocation } from "../../../classes/Player";
import useCoveyAppState from "../../../hooks/useCoveyAppState";

interface MessageLineProps {
    message: ProximityChatMessage,
    myLocation: UserLocation | undefined,
    myID: string
}

const INNER_RADIUS = 100; // how close together players must be to have a message show up as green
const OUTER_RADIUS = 700; // how far apart players must be for the message to stop getting more transparent
const CUTOFF = 0.1;

/** Calculate what the message opacity should be based off of the distance between the message sender/recipient. */
const calculateOpacity = (diff: number): number => {
    let result;
    if (diff < INNER_RADIUS) {
        result = 1;
    } else if (diff >= INNER_RADIUS && diff < OUTER_RADIUS) {
        result = ((OUTER_RADIUS - INNER_RADIUS) - (diff - INNER_RADIUS)) / (OUTER_RADIUS - INNER_RADIUS);
    } else {
        result = 0;
    }
    if (result < CUTOFF) {
        return CUTOFF;
    }
    return result;
}

/** Display a single message in the ChatWindow component. */
function MessageLine(props: MessageLineProps): JSX.Element {
    const {message, myLocation, myID} = props;

    // calculate how far apart the two players are
    const loc1 = message.authorLocation;
    let diff;
    if (!myLocation) {
        diff = 0;
    } else {
        const yDiff = Math.abs(loc1.y - myLocation.y);
        const xDiff = Math.abs(loc1.x - myLocation.x);
        diff = Math.sqrt(yDiff * yDiff + xDiff * xDiff);
    }

    const opacity = useRef<number>(calculateOpacity(diff));
    const background = useRef<string | undefined>();

    // set the message background to green if the players are within the inner radius of each other
    if (diff < INNER_RADIUS && message.author !== myID) {
        background.current = '#9cfc9a';
    }

    return <Flex marginTop={0.5} marginBottom={0.5} borderRadius={10} backgroundColor={background.current}>
        <Flex marginTop={0.5} marginBottom={0.5} marginLeft={2} marginRight={2} opacity={opacity.current} >
            {`${message.userName} ${myID === message.author ? "(You)" : ""}: ${message.body}`}
        </Flex>
    </Flex>;
}

const MAX_NUM_MESSAGES = 6; // how many of the most recent messages to display to the user.

/** Display new messages as they are sent out by players. */
export default function ChatWindow(): JSX.Element {
    const [messageList, setMessageList] = React.useState<ProximityChatMessage[]>([]);
    const [newMessage, setNewMessage] = React.useState<ProximityChatMessage>();
    const { socket, myPlayerID } = useCoveyAppState();
    // using a ref here instead of state to avoid unecessary re-renders
    const myLocation = useRef<UserLocation>();

    // Create a completely fresh copy of the list of messages to avoid mutating state illegally
    const deepCopyMessages = (list: ProximityChatMessage[]): ProximityChatMessage[] => {
        const copy: ProximityChatMessage[] = [];
        list.forEach((m: ProximityChatMessage) => {
            const newMess: ProximityChatMessage = {
                author: m.author,
                userName: m.userName,
                authorLocation: m.authorLocation,
                townID: m.townID,
                body: m.body,
                dateSent: m.dateSent,
            }
            copy.push(newMess);
        })
        return copy;
    }

    // Initialize the message receiving socket
    useEffect(() => {
        socket?.on('proximityMessageRecieved', (m: ProximityChatMessage) => {
            setNewMessage(m);
        });
    }, [socket]);

    // Initialize the player movement socket
    useEffect(() => {
        socket?.on('playerMoved', (p: ServerPlayer) => {
            if (p._id === myPlayerID) {
                myLocation.current = p.location;
            }
        });
    }, [myPlayerID, socket]);

    // Whenever a new message is registered, add that to the total list of messages (messageList),
    // and trigger a component re-render.
    useEffect(() => {
        const newMessages: ProximityChatMessage[] = deepCopyMessages(messageList);
        if (newMessages.length > MAX_NUM_MESSAGES) {
            newMessages.splice(0, 1);
        }
        if (newMessage) {newMessages.push(newMessage);}
        setMessageList(newMessages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newMessage]);

    // create a deep copy of the current user location so that each MessageLine component doesn't keep a reference to the state object.
    const staticMyLocation: UserLocation | undefined = myLocation.current ? {
        x: myLocation.current.x,
        y: myLocation.current.y,
        rotation: myLocation.current.rotation,
        moving: myLocation.current.moving
    } : undefined;

    return  <Flex justifyContent='flex-end'>
        {messageList.length !== 0 ? <Flex 
            style={{position: 'absolute', bottom: 340, left: 18, 
            backgroundColor: 'white', borderRadius: 10, flexDirection: 'column'}}
            shadow="lg" >
            <Flex style={{flexDirection: 'column'}} marginLeft={3} marginRight={3} marginTop={1} marginBottom={1}>
                {messageList.map((m: ProximityChatMessage) => <MessageLine key={`${m.author}${m.dateSent}`} message={m} myLocation={staticMyLocation} myID={myPlayerID}/>)}
            </Flex>
        </Flex> : <></>}
    </Flex>;
 }